// NOTE: NSAI-5881 — validation for the /app-config deployment block.
//
// The block is untrusted input: the mobile app can be pointed at any server,
// so whatever a server says about itself is checked here, once, at the decode
// boundary, before a client acts on it. Three rules from the multi-tenant
// design: only https URLs survive, an unknown flavor reads as the default
// brand, and one entry that does not fit is dropped on its own instead of
// taking the rest of the block with it. A block whose core shape is wrong is
// rejected whole, which leaves the client exactly where an older server would.

import { z } from "zod";

import type { IAppConfigDeployment } from "../types/app-config";
import { ESocialAuthProvider } from "../types/auth";

const DEFAULT_FLAVOR_KEY = "nodeshift";

// NOTE: Major.minor.patch only — the mobile version gate compares numbers, and
// a pre-release suffix it cannot order is better ignored than guessed at.
const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/;

const SOCIAL_AUTH_PROVIDERS: ReadonlySet<string> = new Set(
  Object.values(ESocialAuthProvider),
);

const isSocialAuthProvider = (value: unknown): value is ESocialAuthProvider =>
  typeof value === "string" && SOCIAL_AUTH_PROVIDERS.has(value);

const httpsUrlSchema = z
  .string()
  .url()
  .refine((value) => value.startsWith("https://"));

const optionalHttpsUrlSchema = httpsUrlSchema.optional().catch(undefined);

// NOTE: NSAI-5890 — a base a path is appended to, so a query or a fragment
// would end up in the middle of every link built on it.
const URL_QUERY_OR_FRAGMENT_PATTERN = /[?#]/;

// NOTE: NSAI-5890 — the whole entry goes when its base is unusable, so a
// client never holds a `links` without a `shareBaseUrl` and falls back to its
// own origin, exactly as with a server that sends none.
const optionalLinksSchema = z
  .object({
    shareBaseUrl: httpsUrlSchema.refine(
      (value) => !URL_QUERY_OR_FRAGMENT_PATTERN.test(value),
    ),
  })
  .optional()
  .catch(undefined);

// NOTE: NSAI-5884 — the server publishes only a PostHog project key, which can
// only write events (POSTHOG_PROJECT_KEY_PREFIX in the server's deployment
// constants). Anything else — a personal API key, a garbled value — is
// dropped here, so no client hands it to the SDK.
const POSTHOG_PROJECT_KEY_PREFIX = "phc_";

const optionalPosthogProjectKeySchema = z
  .string()
  .startsWith(POSTHOG_PROJECT_KEY_PREFIX)
  .optional()
  .catch(undefined);

const aiProviderSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  isSelfHosted: z.boolean(),
});

const deploymentSchema = z.object({
  isOnPremise: z.boolean(),
  flavor: z.object({
    key: z.enum(["nodeshift", "oplot"]).catch(DEFAULT_FLAVOR_KEY),
    appName: z.string().min(1),
    companyName: z.string().min(1),
    defaultLanguage: z.string().min(1),
    logoUrl: optionalHttpsUrlSchema,
  }),
  auth: z.object({
    isSignUpEnabled: z.boolean(),
    isSsoVisible: z.boolean(),
    // NOTE: A field that is not even a list reads as "no social sign-in" —
    // the direction that cannot offer a flow the server refuses.
    socialProviders: z
      .array(z.unknown())
      .catch([])
      .transform((values) => values.filter(isSocialAuthProvider)),
    isPasswordResetSelfService: z.boolean(),
  }),
  // NOTE: Deliberately no field-level fallback here, unlike socialProviders. An
  // empty list reads as "every model is self-hosted" to the AI disclosure, the
  // wrong direction to be wrong in, so a providers field that is not a list
  // fails the whole block and leaves the client on its own defaults.
  ai: z.object({
    providers: z.array(z.unknown()).transform((values) =>
      values.flatMap((value) => {
        const parsed = aiProviderSchema.safeParse(value);

        return parsed.success ? [parsed.data] : [];
      }),
    ),
  }),
  analytics: z.object({
    isEnabled: z.boolean(),
    posthogKey: optionalPosthogProjectKeySchema,
    posthogHost: optionalHttpsUrlSchema,
  }),
  legal: z.object({
    privacyPolicyUrl: optionalHttpsUrlSchema,
    supportEmail: z.string().email().optional().catch(undefined),
    termsUrl: optionalHttpsUrlSchema,
  }),
  policies: z.object({
    isAccountDeletionEnabled: z.boolean(),
  }),
  minMobileAppVersion: z
    .string()
    .regex(SEMVER_PATTERN)
    .optional()
    .catch(undefined),
  links: optionalLinksSchema,
});

// NOTE: Returns undefined for anything that is not a usable block — including
// a server that sent none at all — so callers have one "no manifest" case.
export const parseAppConfigDeployment = (
  value: unknown,
): IAppConfigDeployment | undefined => {
  const parsed = deploymentSchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
};
